import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ShallowFractalCanvas } from './ShallowFractalCanvas';
import { getLoadedDeepZoom, loadDeepZoom, needsDeepZoom } from './deepZoomLoader';
import { useStableColorParams } from './fullscreenControl';
import { releasePreviewBackground } from './previewBackground';
import { serializeColorFunc } from './useFractalWorker';

/** Load the high-precision renderer only once a canvas needs it. */
export function FractalCanvas(props) {
  const colorParams = useStableColorParams(
    serializeColorFunc,
    props.colorFunc,
    props.deepColorRange,
  );
  const preloadedMod = props.initialView && !loadDeepZoom?.mock ? getLoadedDeepZoom() : null;
  const [deep, setDeep] = useState(() => (
    preloadedMod?.DeepFractalCanvas
      ? {
        Component: preloadedMod.DeepFractalCanvas,
        initialView: props.initialView,
        initialHistory: [],
        initialMotion: null,
      }
      : null
  ));
  const [status, setStatus] = useState(null);
  const [generation, setGeneration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isZenMode, setIsZenMode] = useState(false);
  const handleFullscreenChange = useCallback(nextFullscreen => {
    setIsFullscreen(nextFullscreen);
    if (!nextFullscreen) setIsZenMode(false);
  }, []);
  const latest = useRef(null);
  const pendingView = useRef(null);
  const previewCanvasRef = useRef(null);
  const completedFramesRef = useRef(new Map());
  const shallowBacking = useRef({ width: props.width, height: props.height });
  const shallowAspect = useRef(props.width / props.height);
  const loadedModule = useRef(preloadedMod);
  const gestureActive = useRef(false);
  const handoffFrame = useRef(null);
  const handoffComplete = useRef(Boolean(deep));
  const loading = useRef(false);
  const alive = useRef(false);
  const request = useRef(0);
  const failed = useRef(false);
  const cancelLoad = useCallback(() => {
    request.current++;
    loading.current = false;
    if (handoffFrame.current !== null) {
      cancelAnimationFrame(handoffFrame.current);
      handoffFrame.current = null;
    }
  }, []);

  const releasePreviewCanvas = useCallback(() => {
    if (!previewCanvasRef.current) return;
    releasePreviewBackground(previewCanvasRef.current);
    previewCanvasRef.current.width = 0;
    previewCanvasRef.current.height = 0;
    previewCanvasRef.current = null;
  }, []);

  const releaseCompletedFrames = useCallback(() => {
    const completedFrames = completedFramesRef.current;
    for (const { snapshot } of completedFrames.values()) {
      if (snapshot?.canvas) {
        releasePreviewBackground(snapshot.canvas);
        snapshot.canvas.width = 0;
        snapshot.canvas.height = 0;
      }
    }
    completedFrames.clear();
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      cancelLoad();
      releasePreviewCanvas();
      releaseCompletedFrames();
    };
  }, [cancelLoad, releasePreviewCanvas, releaseCompletedFrames]);

  const scheduleHandoff = useCallback(() => {
    if (!alive.current || handoffComplete.current || !loadedModule.current || !latest.current || gestureActive.current
      || handoffFrame.current !== null) return;
    const token = request.current;
    // Let input and its React state/history updates finish before replacing the
    // element. Read the latest requested camera, independently of its preview.
    handoffFrame.current = requestAnimationFrame(() => {
      handoffFrame.current = null;
      if (!alive.current || request.current !== token || gestureActive.current || !latest.current) return;
      handoffComplete.current = true;
      // Carry the remaining eased wheel motion across the renderer switch.
      // Snapshot at commit so intervening input and animation frames count.
      const { getPendingMotion, ...initial } = latest.current;
      const initialMotion = getPendingMotion?.() || null;
      // Commit the camera snapshot and DOM replacement together. Otherwise an
      // input event can still reach the old canvas after its snapshot is taken.
      flushSync(() => {
        setStatus(null);
        setDeep({ Component: loadedModule.current.DeepFractalCanvas, ...initial, initialMotion });
      });
    });
  }, []);

  const start = useCallback(() => {
    if (loading.current || !latest.current) return;
    failed.current = false;
    if (loadedModule.current) {
      scheduleHandoff();
      return;
    }
    loading.current = true;
    setStatus('Loading deep zoom…');
    const token = ++request.current;
    loadDeepZoom().then(module => {
      if (!alive.current || request.current !== token) return;
      loading.current = false;
      loadedModule.current = module;
      setStatus(null);
      scheduleHandoff();
    }).catch(() => {
      if (!alive.current || request.current !== token) return;
      loading.current = false;
      failed.current = true;
      setStatus('Deep zoom could not load. Check your connection and retry.');
    });
  }, [scheduleHandoff]);

  const handlePendingView = useCallback((xRange, yRange, history, getPendingMotion) => {
    if (latest.current?.initialView || colorParams?.mode === 'nebulabrot') return;
    if (!needsDeepZoom(xRange, yRange, shallowBacking.current.width, shallowBacking.current.height)) {
      pendingView.current = null;
      // A user can zoom back out while the chunk is loading.
      if (latest.current || loading.current || failed.current) {
        cancelLoad();
        releasePreviewCanvas();
        failed.current = false;
        latest.current = null;
        setStatus(null);
      }
      return;
    }
    pendingView.current = { xRange, yRange };
    latest.current = {
      ...latest.current,
      initialRanges: { xRange, yRange },
      initialAspect: shallowAspect.current,
      getPendingMotion,
      initialHistory: history.map(({ xRange: x, yRange: y }) => ({ xRange: x, yRange: y })),
    };
    if (!failed.current) start();
  }, [start, cancelLoad, releasePreviewCanvas, colorParams]);

  const handleBackingResize = useCallback((width, height, aspect) => {
    shallowBacking.current = { width, height };
    if (Number.isFinite(aspect) && aspect > 0) {
      shallowAspect.current = aspect;
    } else if (width > 0 && height > 0) {
      shallowAspect.current = width / height;
    }
  }, []);

  const handleView = useCallback((canvas, xRange, yRange, history) => {
    if (latest.current?.initialView) return;
    if (!pendingView.current) handlePendingView(xRange, yRange, history);
    if (!latest.current || !(canvas?.width > 0 && canvas?.height > 0)) return;
    const previewCanvas = previewCanvasRef.current || (
      previewCanvasRef.current = document.createElement('canvas')
    );
    if (previewCanvas.width !== canvas.width) previewCanvas.width = canvas.width;
    if (previewCanvas.height !== canvas.height) previewCanvas.height = canvas.height;
    // Called synchronously after the GPU draw, before its buffer is discarded.
    previewCanvas.getContext('2d')?.drawImage(canvas, 0, 0);
    latest.current = {
      ...latest.current,
      previewRanges: { xRange, yRange },
      initialAspect: shallowAspect.current,
      previewCanvas,
      initialHistory: history.map(({ xRange: x, yRange: y }) => ({ xRange: x, yRange: y })),
    };
    scheduleHandoff();
  }, [handlePendingView, scheduleHandoff]);

  const handleGestureActivity = useCallback(active => {
    gestureActive.current = active;
    if (active && handoffFrame.current !== null) {
      cancelAnimationFrame(handoffFrame.current);
      handoffFrame.current = null;
    }
    if (!active) scheduleHandoff();
  }, [scheduleHandoff]);

  // String coordinates allow callers to open locations beyond Number precision.
  useEffect(() => {
    if (props.initialView) {
      latest.current = { initialView: props.initialView, initialHistory: [] };
      start();
    }
  }, [props.initialView, start, generation]);

  useEffect(() => {
    if (deep) releasePreviewCanvas();
  }, [deep, releasePreviewCanvas]);

  const reset = useCallback(() => {
    cancelLoad();
    releasePreviewCanvas();
    if (props.initialView && loadedModule.current?.DeepFractalCanvas) {
      const nextInitial = { initialView: props.initialView, initialHistory: [] };
      latest.current = nextInitial;
      pendingView.current = null;
      gestureActive.current = false;
      handoffComplete.current = true;
      failed.current = false;
      loading.current = false;
      setDeep({
        Component: loadedModule.current.DeepFractalCanvas,
        ...nextInitial,
        initialMotion: null,
      });
      setStatus(null);
      setGeneration(value => value + 1);
      return;
    }
    if (!props.initialView) {
      releaseCompletedFrames();
    }
    latest.current = null;
    pendingView.current = null;
    gestureActive.current = false;
    handoffComplete.current = false;
    failed.current = false;
    loading.current = false;
    shallowBacking.current = { width: props.width, height: props.height };
    shallowAspect.current = props.width / props.height;
    setDeep(null);
    setStatus(null);
    setGeneration(value => value + 1);
  }, [cancelLoad, releasePreviewCanvas, releaseCompletedFrames, props.initialView, props.width, props.height]);

  if (deep) {
    const { Component, ...initial } = deep;
    return <Component key={generation} {...props} {...initial}
      isFullscreen={isFullscreen} onFullscreenChange={handleFullscreenChange}
      isZenMode={isZenMode} onZenModeChange={setIsZenMode}
      completedFramesRef={completedFramesRef} colorParams={colorParams} onReset={reset} />;
  }
  return <ShallowFractalCanvas {...props} key={generation}
    autoRender={generation > 0}
    isFullscreen={isFullscreen} onFullscreenChange={handleFullscreenChange}
    isZenMode={isZenMode} onZenModeChange={setIsZenMode}
    onViewRendered={handleView} onViewPending={handlePendingView}
    onBackingResize={handleBackingResize}
    onGestureActivity={handleGestureActivity} deepZoomStatus={status}
    onRetryDeepZoom={failed.current ? start : undefined} />;
}
